import React from "react";
import { Box, Typography } from "@mui/material";

export interface AutotaskPsaGuideInnerProps {
  showGuideTitle?: boolean;
}

/**
 * Autotask PSA onboarding/integration guide shared between onboarding and add-integration flows.
 * Grep: Autotask setup guide, API Tracking Identifier, webservicesXX base URL
 */
export const AutotaskPsaGuideInner: React.FC<AutotaskPsaGuideInnerProps> = ({
  showGuideTitle = true,
}) => {
  return (
    <>
      {showGuideTitle ? (
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 15,
            color: "#1a1a1a",
            mb: 1.5,
          }}
        >
          Setup Guide — Autotask PSA
        </Typography>
      ) : null}

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        Base URL
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          If you log in at something like{" "}
          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
            https://ww##.autotask.net/...
          </Typography>
          , your REST base is typically{" "}
          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
            https://webservices##.autotask.net/atservicesrest
          </Typography>
          .
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        Username + Password (REST Key + Secret)
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In Autotask, go to Settings → Admin → Account Settings &amp; Users → Resources/Users (HR) → Resources/Users.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Click New dropdown → New API User.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Fill in the user details (name/email), and set Security Level to something like API User (System).
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In the Credentials section:
          <Box component="ol" sx={{ m: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555" }}>
              Click <strong>Generate Key</strong> → this populates Username (Key).
            </Typography>
            <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555" }}>
              Click <strong>Generate Secret</strong> → this populates Password (Secret).
            </Typography>
          </Box>
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        API Integration Code (API Tracking Identifier)
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In the API User window, scroll to <strong>API Tracking Identifier</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Choose <strong>Custom (Internal Integration)</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Enter Internal Integration Name (any descriptive name).
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Copy the generated <strong>Tracking Identifier</strong> value shown below.
        </Typography>
      </Box>
    </>
  );
};
